class TopicSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.prev = this.querySelector('[data-prev]');
    this.next = this.querySelector('[data-next]');
    this.current = this.querySelector('[data-current]');
    this.sheet = this.querySelector('[data-sheet]');
    if (!this.track) return;

    this.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-toggle]');
      if (btn) {
        const card = btn.closest('[data-card]');
        if (this.mode === 'sheet' && this.sheet) this.openSheet(card);
        else this.toggle(card);
        return;
      }
      if (e.target.closest('[data-sheet-close]')) this.sheet.close();
      else if (e.target.closest('[data-sheet-prev]')) this.sheetStep(-1);
      else if (e.target.closest('[data-sheet-next]')) this.sheetStep(1);
    });

    this.sheet?.addEventListener('close', () => this.onSheetClose());

    // Escape closes the open card and hands focus back to its toggle; the sheet closes itself.
    this.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || e.target.closest('[data-sheet]')) return;
      const card = e.target.closest('[data-card][data-open]');
      if (!card) return;
      this.cancel?.();
      this.set(card, false);
      card.querySelector('[data-toggle]').focus();
    });

    this.prev?.addEventListener('click', () => { if (!this.prev.classList.contains('disabled')) this.step(-1); });
    this.next?.addEventListener('click', () => { if (!this.next.classList.contains('disabled')) this.step(1); });

    this.onScroll = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); });
    };
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.track.addEventListener('transitionend', this.onScroll);
    this.resize = new ResizeObserver(this.onScroll);
    this.resize.observe(this.track);
    this.update();
    document.fonts?.ready.then(() => this.querySelectorAll('[data-card]').forEach((c) => this.sizeFact(c)));

    // Off-screen cards sit sideways in the track, where lazy images never start; load them as the section nears.
    this.preload = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      this.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
      this.preload.disconnect();
    }, { rootMargin: '100% 0px' });
    this.preload.observe(this);
  }

  // Closed, the fact box is the label's size; open, the list's. CSS can't size to either, so measure.
  sizeFact(card) {
    const tag = card.querySelector('.br-topic-slider__fact-tag');
    const list = card.querySelector('.br-topic-slider__fact-list');
    if (tag?.offsetWidth) card.style.setProperty('--ts-chip-w', `${tag.offsetWidth}px`);
    if (list?.offsetHeight) card.style.setProperty('--ts-fact-h', `${list.offsetHeight}px`);
  }

  disconnectedCallback() {
    this.resize?.disconnect();
    this.preload?.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  // Set by the container query: cards open in place from 1024, the sheet below.
  get mode() {
    return getComputedStyle(this.track).getPropertyValue('--ts-open-mode').trim() === 'card' ? 'card' : 'sheet';
  }

  openSheet(card) {
    this.cards = [...this.querySelectorAll('[data-card]')];
    this.sheetIndex = this.cards.indexOf(card);
    this.fillSheet();
    card.querySelector('[data-toggle]').setAttribute('aria-expanded', 'true');
    this.sheet.showModal();
  }

  // The sheet shows a copy of the card, opened, with its ids stripped so none repeat.
  fillSheet() {
    const src = this.cards[this.sheetIndex];
    const copy = src.cloneNode(true);
    copy.removeAttribute('data-card');
    copy.removeAttribute('aria-labelledby');
    copy.setAttribute('data-open', '');
    copy.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    copy.querySelectorAll('[data-more]').forEach((el) => { el.inert = false; });
    copy.querySelector('.br-topic-slider__title').id = this.sheet.dataset.titleId;
    // Oura's order: title, text, the fact as a card, then the button and arrows on one row.
    const aside = copy.querySelector('.br-topic-slider__aside');
    if (aside) copy.querySelector('.br-topic-slider__more-inner').append(aside);
    const foot = document.createElement('div');
    foot.className = 'br-topic-slider__sheet-foot';
    const cta = copy.querySelector('.br-topic-slider__cta');
    if (cta) foot.append(cta);
    const close = copy.querySelector('[data-toggle]');
    ['data-toggle', 'aria-expanded', 'aria-controls'].forEach((a) => close.removeAttribute(a));
    close.setAttribute('aria-label', 'Close');
    close.dataset.sheetClose = '';
    const nav = this.sheet.querySelector('[data-sheet-nav]');
    if (nav) {
      foot.append(nav);
      nav.querySelector('[data-sheet-prev]').disabled = this.sheetIndex === 0;
      nav.querySelector('[data-sheet-next]').disabled = this.sheetIndex === this.cards.length - 1;
    }
    copy.append(foot);
    this.sheet.querySelector('[data-sheet-body]').replaceChildren(copy);
    this.sheet.scrollTop = 0;
  }

  sheetStep(dir) {
    const to = this.sheetIndex + dir;
    if (to < 0 || to >= this.cards.length) return;
    this.cards[this.sheetIndex].querySelector('[data-toggle]').setAttribute('aria-expanded', 'false');
    this.sheetIndex = to;
    this.cards[to].querySelector('[data-toggle]').setAttribute('aria-expanded', 'true');
    this.fillSheet();
    // Moving the nav into the new copy drops focus; put it back, or on the other arrow at an end.
    const prev = this.sheet.querySelector('[data-sheet-prev]');
    const next = this.sheet.querySelector('[data-sheet-next]');
    const want = dir > 0 ? next : prev;
    (want.disabled ? (dir > 0 ? prev : next) : want).focus();
    const title = this.cards[to].querySelector('.br-topic-slider__title').textContent.trim().replace(/[.!?]+$/, '');
    this.sheet.querySelector('[data-sheet-status]').textContent = `${title}. Topic ${to + 1} of ${this.cards.length}.`;
  }

  // Focus returns to the + of the topic last shown, and the track brings that card into view.
  onSheetClose() {
    const card = this.cards?.[this.sheetIndex];
    this.querySelectorAll('[data-toggle]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
    this.sheet.querySelector('[data-sheet-status]').textContent = '';
    if (!card) return;
    card.querySelector('[data-toggle]').focus({ preventScroll: true });
    this.glide(card.parentElement);
  }

  get behavior() {
    return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  toggle(card) {
    if (card.hasAttribute('data-open')) {
      this.cancel?.();
      this.set(card, false);
      return;
    }
    // Grows and slides to the gutter at once, like Oura, on one spring.
    this.glide(card.parentElement, () => {
      this.closeAll();
      this.set(card, true);
    });
  }

  closeAll() {
    const open = this.querySelectorAll('[data-card][data-open]');
    open.forEach((c) => this.set(c, false));
    return open.length > 0;
  }

  // One rAF controller owns the slide's on-screen left edge; reading offsetLeft each frame cancels any width change beside it.
  glide(slide, change) {
    this.cancel?.(true);
    const gutter = parseFloat(getComputedStyle(this.track).paddingLeft) || 0;
    const startLeft = slide.offsetLeft - this.track.scrollLeft;
    const resized = change?.() !== false;
    this.target = slide;

    if (this.behavior === 'auto') {
      delete this.track.dataset.moving;
      requestAnimationFrame(() => { this.track.scrollLeft = this.clampScroll(slide.offsetLeft - gutter); this.target = null; });
      return;
    }

    // Critically damped spring (Apple: damping 1, response 0.4s); an interrupted glide hands its velocity on.
    const omega = (2 * Math.PI) / 0.4;
    const d0 = startLeft - gutter;
    const v0 = performance.now() - (this.velAt || 0) < 50 ? this.vel || 0 : 0;
    // The card starts growing after the fact box (CSS --ts-lead), so hold through both.
    const hold = resized ? 860 : 0;
    const t0 = performance.now();
    let prevLeft = startLeft;
    let prevNow = t0;
    let raf = 0;
    let stopped = false;
    const stop = () => { stopped = true; };
    // A handoff keeps snapping paused; re-enabling it for one style read snaps the track back.
    const finish = (handoff = false) => {
      cancelAnimationFrame(raf);
      this.track.removeEventListener('pointerdown', stop);
      this.track.removeEventListener('wheel', stop);
      if (!handoff) delete this.track.dataset.moving;
      if (this.cancel === finish) { this.cancel = null; this.target = null; }
    };
    const frame = (now) => {
      // A touch or wheel mid-move hands the track back to the hand.
      if (stopped) { finish(); return; }
      // rAF time can predate t0 by a few ms; a negative time would flick the first frame.
      const sec = Math.max(0, now - t0) / 1000;
      const d = (d0 + (v0 + omega * d0) * sec) * Math.exp(-omega * sec);
      const settled = Math.abs(d) < 0.5;
      const left = settled ? gutter : gutter + d;
      this.track.scrollLeft = this.clampScroll(slide.offsetLeft - left);
      const dt = Math.max(0, now - prevNow) / 1000;
      if (dt > 0) { this.vel = (left - prevLeft) / dt; this.velAt = now; }
      prevLeft = left;
      prevNow = now;
      if (settled && now - t0 >= hold) { finish(); return; }
      raf = requestAnimationFrame(frame);
    };
    this.track.dataset.moving = '';
    this.track.addEventListener('pointerdown', stop, { once: true });
    this.track.addEventListener('wheel', stop, { once: true, passive: true });
    this.cancel = finish;
    raf = requestAnimationFrame(frame);
  }

  clampScroll(x) {
    return Math.max(0, Math.min(x, this.track.scrollWidth - this.track.clientWidth));
  }

  set(card, open) {
    if (open) this.sizeFact(card);
    card.toggleAttribute('data-open', open);
    card.querySelector('[data-toggle]').setAttribute('aria-expanded', String(open));
    card.querySelectorAll('[data-more]').forEach((el) => { el.inert = !open; });
  }

  // One card per press; an open card closes as the track moves on, as on Oura, without its overshoot.
  step(dir) {
    const slides = [...this.track.querySelectorAll('.br-topic-slider__slide')];
    const max = this.track.scrollWidth - this.track.clientWidth;
    const from = this.target ? slides.indexOf(this.target) : this.index(this.track.scrollLeft, max, dir < 0) - 1;
    const to = slides[Math.min(slides.length - 1, Math.max(0, from + dir))];
    if (!to || to === this.target) return;
    this.glide(to, () => this.closeAll());
  }

  // The slide sitting nearest the gutter; at the end of the track the counter shows the last one.
  index(scrollLeft, max, atGutter = false) {
    const slides = this.track.querySelectorAll('.br-topic-slider__slide');
    if (!atGutter && max > 0 && scrollLeft >= max - 2) return slides.length;
    const pad = parseFloat(getComputedStyle(this.track).paddingLeft) || 0;
    let best = 0;
    let gap = Infinity;
    slides.forEach((s, i) => {
      const d = Math.abs(s.offsetLeft - pad - scrollLeft);
      if (d < gap) { gap = d; best = i; }
    });
    return best + 1;
  }

  update() {
    // A resize across 1024 hands an open topic to the other mode's closed state.
    const mode = this.mode;
    if (mode === 'sheet' && this.querySelector('[data-card][data-open]')) this.closeAll();
    if (mode === 'card' && this.sheet?.open) this.sheet.close();
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const max = scrollWidth - clientWidth;
    if (this.current) this.current.textContent = String(this.index(scrollLeft, max));
    if (!this.prev || !this.next) return;
    // The image slider's .disabled class; aria-disabled keeps the button focusable, so focus never drops.
    const atStart = scrollLeft <= 2;
    const atEnd = scrollLeft >= max - 2;
    this.prev.classList.toggle('disabled', atStart);
    this.next.classList.toggle('disabled', atEnd);
    this.prev.setAttribute('aria-disabled', String(atStart));
    this.next.setAttribute('aria-disabled', String(atEnd));
  }
}

if (!customElements.get('topic-slider')) {
  customElements.define('topic-slider', TopicSlider);
}
